<template>
  <div class="customer-schemes-page-wrapper m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Page Title & Identity -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-bookmark-multiple-outline</v-icon>
          </div>
          <div>
            <div class="page-title">CUSTOMER SCHEMES DIRECTORY</div>
            <div class="page-subtitle">Track customer enrollments, recurring installment payments &amp; maturity statuses</div>
          </div>
        </div>

        <!-- Right: Action Bar (Filter + Search + Refresh + Enlist + New Customer) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <!-- Filter by Scheme -->
          <div class="filter-select-wrap">
            <v-autocomplete
              background-color="white"
              :items="List1"
              v-model="schemefilter"
              placeholder="All Gold Schemes"
              item-text="goldSchemeName"
              item-value="_id"
              outlined
              clearable
              hide-details
              dense
              class="filter-input"
            ></v-autocomplete>
          </div>

          <!-- Search Input -->
          <div class="search-input-wrap">
            <v-text-field
              v-model="keyword"
              clearable
              background-color="white"
              color="#9a6b27"
              hide-details
              placeholder="Search by name, phone or scheme no..."
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              class="search-input"
            ></v-text-field>
          </div>

          <!-- Refresh Button -->
          <v-btn
            icon
            small
            title="Refresh Directory"
            class="refresh-icon-btn"
            @click="getCustomerSchemeList()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <!-- New Customer Button -->
          <v-btn
            outlined
            color="#9a6b27"
            class="btn-gold-outlined text-capitalize"
            @click="openAddCustomerModal()"
          >
            <v-icon left small color="#9a6b27">mdi-account-plus-outline</v-icon>
            New Customer
          </v-btn>

          <!-- Enlist In Scheme Button -->
          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="openEnlistModal()"
          >
            <v-icon left small>mdi-plus</v-icon>
            Enlist Customer
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- 1. Total Enrollments -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL ENROLLMENTS</div>
          <div class="kpi-value">{{ totalEnrollmentsCount }}</div>
          <div class="kpi-hint">Total registered accounts</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-account-group-outline</v-icon>
        </div>
      </div>

      <!-- 2. Active Accounts -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">ACTIVE ACCOUNTS</div>
          <div class="kpi-value">{{ activeAccountsCount }}</div>
          <div class="kpi-hint">Currently running plans</div>
        </div>
        <div class="kpi-icon-pill" style="background: #ecfdf5; color: #059669;">
          <v-icon size="20" color="#059669">mdi-check-circle-outline</v-icon>
        </div>
      </div>

      <!-- 3. Total Realized Amount -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL REALIZED VALUE</div>
          <div class="kpi-value">₹{{ formatCurrency(totalRealizedAmount) }}</div>
          <div class="kpi-hint">Cumulative matured value</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
          <v-icon size="20" color="#ca8a04">mdi-gold</v-icon>
        </div>
      </div>

      <!-- 4. Closed / Matured Accounts -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">CLOSED / MATURED</div>
          <div class="kpi-value">{{ closedAccountsCount }}</div>
          <div class="kpi-hint">Completed or redeemed</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fdf4ff; color: #a855f7;">
          <v-icon size="20" color="#a855f7">mdi-lock-check-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Master Data Table Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <!-- Data Table View -->
      <div v-if="schemeList && schemeList.length > 0">
        <v-simple-table class="pro-master-table m-stack-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 50px;">S.No.</th>
                <th class="text-left" style="min-width: 210px;">Customer Details</th>
                <th class="text-left" style="min-width: 200px;">Scheme Details</th>
                <th class="text-left" style="width: 170px;">Duration &amp; Dates</th>
                <th class="text-right" style="width: 140px;">Total Amount</th>
                <th class="text-center" style="width: 100px;">Status</th>
                <th class="text-center" style="width: 140px;">Payment Action</th>
                <th class="text-center" style="width: 90px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(value, i) in schemeList"
                :key="value._id || i"
                class="master-table-row"
              >
                <!-- S.No. -->
                <td data-label="S.No." class="text-muted font-weight-medium">
                  {{ page == 1 ? i + 1 : i + 1 + 20 * (page - 1) }}
                </td>

                <!-- Customer Profile -->
                <td data-label="Customer">
                  <div class="d-flex align-center">
                    <div class="customer-avatar mr-3">
                      {{ getInitials(value.customerId ? value.customerId.customerName : '') }}
                    </div>
                    <div>
                      <div class="customer-name-text">
                        {{ (value.customerId && value.customerId.customerName) || '—' }}
                      </div>
                      <div class="customer-phone-text">
                        <v-icon x-small color="#64748b" class="mr-1">mdi-phone-outline</v-icon>
                        <span>{{ (value.customerId && value.customerId.mobileNumber) || 'No Phone' }}</span>
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Scheme Details -->
                <td data-label="Scheme">
                  <div class="d-flex flex-column" style="gap: 3px;">
                    <div class="d-flex align-center">
                      <span class="scheme-no-pill mr-2">
                        {{ value.schemeNumber || '—' }}
                      </span>
                    </div>
                    <div class="scheme-name-text">
                      {{ (value.goldSchemeId && value.goldSchemeId.goldSchemeName) || '—' }}
                    </div>
                    <div v-if="value.goldSchemeId && value.goldSchemeId.yearlyIntrest" class="text-caption text-muted">
                      {{ value.goldSchemeId.yearlyIntrest }}% p.a. yield
                    </div>
                  </div>
                </td>

                <!-- Duration & Dates -->
                <td data-label="Duration">
                  <div class="date-info">
                    <div class="d-flex align-center">
                      <span class="text-muted mr-1" style="font-size: 11px;">Start:</span>
                      <span class="font-weight-medium">{{ formatDate(value.startDate) }}</span>
                    </div>
                    <div class="d-flex align-center mt-1">
                      <span class="text-muted mr-1" style="font-size: 11px;">Mature:</span>
                      <span class="font-weight-medium" style="color: #0284c7;">{{ formatDate(value.matureDate) }}</span>
                    </div>
                  </div>
                </td>

                <!-- Total Amount -->
                <td data-label="Total Amount" class="text-right">
                  <span v-if="value.finalTotalAmount && value.finalTotalAmount > 0" class="total-amt-text font-weight-bold">
                    ₹{{ formatCurrency(value.finalTotalAmount) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                  <div v-if="value.redeemedAmount && value.redeemedAmount > 0" class="text-caption text-muted">
                    Balance ₹{{ formatCurrency(Math.max(0, value.finalTotalAmount - value.redeemedAmount)) }}
                  </div>
                </td>

                <!-- Status -->
                <td data-label="Status" class="text-center">
                  <span
                    class="status-chip"
                    :class="value.schemeStatus === 'Active' ? 'status-active' : value.schemeStatus === 'Closed' ? 'status-closed' : 'status-matured'"
                  >
                    {{ value.schemeStatus || 'Active' }}
                  </span>
                </td>

                <!-- Payment Action -->
                <td data-label="Payment" class="text-center">
                  <v-btn
                    v-if="value.isPaid == true && value.isschemebalanceAdded == true"
                    small
                    outlined
                    color="#059669"
                    class="btn-row-action remitted-btn text-capitalize"
                    @click="$router.push('/GoldSchemePayment?id=' + value._id)"
                  >
                    <v-icon left x-small>mdi-check-all</v-icon>
                    Remitted
                  </v-btn>
                  <v-btn
                    v-else-if="(value.isPaid == true || value.schemeStatus == 'Closed') && value.totalPaidAmount > 0 && value.isschemebalanceAdded == false"
                    small
                    color="#d97706"
                    dark
                    class="btn-row-action remit-pay-btn text-capitalize elevation-1"
                    @click="AddToBalance(value._id)"
                  >
                    <v-icon left x-small>mdi-cash-refund</v-icon>
                    Remit Pay
                  </v-btn>
                  <v-btn
                    v-else
                    small
                    color="#9a6b27"
                    dark
                    class="btn-row-action pay-btn text-capitalize elevation-1"
                    @click="$router.push('/GoldSchemePayment?id=' + value._id)"
                  >
                    <v-icon left x-small>mdi-credit-card-outline</v-icon>
                    Payment
                  </v-btn>
                </td>

                <!-- Actions -->
                <td data-label="Actions" class="text-center">
                  <div class="d-flex align-center justify-center" style="gap: 4px;">
                    <v-btn
                      v-if="value.schemeStatus != 'Closed' && value.schemeStatus != 'Matured'"
                      icon
                      x-small
                      title="Close Scheme Early"
                      class="action-icon-btn lock-btn"
                      @click="(closedialog = true), (curId = value._id)"
                    >
                      <v-icon size="15" color="#d97706">mdi-lock-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      title="Delete Customer from Scheme"
                      class="action-icon-btn delete-btn"
                      @click="(deletedialog = true), (curId = value._id)"
                    >
                      <v-icon size="15" color="#dc2626">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- Footer & Pagination -->
        <div class="table-footer-wrap px-4 py-3 d-flex flex-wrap align-center justify-space-between border-t">
          <div class="text-caption text-muted">
            Showing {{ ((page - 1) * 20) + 1 }} to {{ Math.min(page * 20, totalEnrollmentsCount) }} of {{ totalEnrollmentsCount }} enrollments
          </div>
          <v-pagination
            v-if="Pagelength > 1"
            v-model="page"
            :length="Pagelength"
            :total-visible="7"
            color="#9a6b27"
            dense
            class="pro-pagination"
          ></v-pagination>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="32" color="#94a3b8">mdi-account-search-outline</v-icon>
        </div>
        <div class="empty-title">No customer scheme enrollments found</div>
        <div class="empty-subtitle">
          {{ keyword || schemefilter ? 'No records match your filter criteria. Try clearing search or selecting a different scheme.' : 'Enlist your first customer into a savings scheme to begin.' }}
        </div>
        <div class="mt-4" v-if="keyword || schemefilter">
          <v-btn small outlined color="#64748b" @click="keyword = ''; schemefilter = ''; getCustomerSchemeList();">
            <v-icon left small>mdi-close</v-icon>
            Clear Filters
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- ===== ENLISTING (ADD CUSTOMER TO SCHEME) MODAL ===== -->
    <v-dialog v-model="addScheme" max-width="840px" persistent>
      <v-form ref="addcat" v-model="addcat" @submit.prevent>
        <v-card class="modal-card rounded-lg overflow-hidden">
          <!-- Header -->
          <div class="modal-header px-6 py-4 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <div class="modal-icon-pill mr-3" style="background: #fefce8; color: #9a6b27;">
                <v-icon size="18" color="#9a6b27">mdi-bookmark-plus-outline</v-icon>
              </div>
              <div>
                <div class="modal-title">Enlist Customer into Gold Scheme</div>
                <div class="modal-subtitle">Enroll client into recurring gold installment scheme &amp; set schedules</div>
              </div>
            </div>
            <v-btn icon small @click="closeEnlistModal()" class="close-btn">
              <v-icon small color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Body -->
          <div class="px-6 py-4">
            <div class="form-section-title mb-3">Customer &amp; Scheme Selection</div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <div class="d-flex align-center justify-space-between mb-1">
                  <label class="field-label mb-0">Customer <span class="required-star">*</span></label>
                  <v-btn text x-small color="#9a6b27" @click="addcustomerdialogue = true" class="px-1 font-weight-bold">
                    <v-icon x-small left>mdi-plus</v-icon>New Customer
                  </v-btn>
                </div>
                <v-autocomplete
                  background-color="white"
                  :items="customerList"
                  v-model="customerdetails"
                  placeholder="Select customer by name or phone"
                  item-text="customerName"
                  item-value="_id"
                  :filter="customerFilter"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">{{ item.customerName }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.mobileNumber" class="text-caption text-muted">
                        {{ item.mobileNumber }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span>{{ item.customerName }} <small v-if="item.mobileNumber" class="text-muted">({{ item.mobileNumber }})</small></span>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6">
                <label class="field-label">Gold Scheme Plan <span class="required-star">*</span></label>
                <v-select
                  color="#9a6b27"
                  :items="List1"
                  v-model="goldSchemeName"
                  placeholder="Select Scheme"
                  item-text="goldSchemeName"
                  item-value="_id"
                  @input="selectThis(goldSchemeName)"
                  outlined
                  hide-details
                  dense
                  class="pro-input"
                ></v-select>
              </v-col>
            </v-row>

            <div class="form-section-title mb-3 mt-3">Account &amp; Financial Parameters</div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="4">
                <label class="field-label">Scheme Account No.</label>
                <v-text-field
                  v-model="schemeNumber"
                  placeholder="e.g. GS-2026-0001"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="4">
                <label class="field-label">Monthly Deposit (₹)</label>
                <v-text-field
                  v-model="mnthlydpst"
                  placeholder="Deposit installment"
                  type="number"
                  disabled
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="4">
                <label class="field-label">Yearly Interest (%)</label>
                <v-text-field
                  v-model="yrlyintrst"
                  placeholder="Yield rate"
                  type="number"
                  disabled
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="form-section-title mb-3 mt-3">Schedule &amp; Dates</div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <label class="field-label">Starting Date</label>
                <v-menu
                  ref="menu1"
                  v-model="menu1"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="fromDate"
                      outlined
                      dense
                      readonly
                      append-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      color="#9a6b27"
                      hide-details
                      class="pro-input"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="fromDate"
                    color="#9a6b27"
                    no-title
                    @input="menu1 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" sm="4">
                <label class="field-label">Monthly Due Date</label>
                <v-menu
                  ref="menu3"
                  v-model="menu3"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dueDate"
                      outlined
                      dense
                      readonly
                      placeholder="Select Due Date"
                      append-icon="mdi-calendar-clock"
                      v-bind="attrs"
                      v-on="on"
                      color="#9a6b27"
                      hide-details
                      class="pro-input"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dueDate"
                    color="#9a6b27"
                    no-title
                    :min="computedMinDueDate"
                    :max="computedMaxDueDate"
                    @input="menu3 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" sm="4">
                <label class="field-label">Maturity Date</label>
                <v-text-field
                  v-model="secondDate"
                  placeholder="Auto-calculated"
                  disabled
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Actions -->
          <div class="modal-footer px-6 py-3 border-t d-flex justify-end" style="gap: 8px;">
            <v-btn text class="btn-cancel" @click="closeEnlistModal()">Cancel</v-btn>
            <v-btn
              color="#9a6b27"
              class="btn-primary text-capitalize elevation-1"
              @click="addToScheme()"
              :loading="appLoading"
            >
              <v-icon left small>mdi-check</v-icon>
              Enroll in Scheme
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- ===== CLOSE SCHEME CONFIRMATION DIALOG ===== -->
    <v-dialog v-model="closedialog" max-width="460px">
      <v-card class="modal-card rounded-lg pa-6">
        <div class="text-center">
          <div class="lock-icon-box mb-3 mx-auto">
            <v-icon size="26" color="#d97706">mdi-lock-alert-outline</v-icon>
          </div>
          <div class="delete-title mb-1">Close Gold Scheme Account?</div>
          <div class="delete-subtitle">
            Early closure locks the redeemable amount to what the customer has currently deposited and cancels remaining installments.
          </div>
        </div>
        <div class="d-flex align-center justify-end mt-6" style="gap: 8px;">
          <v-btn text class="btn-cancel" @click="closedialog = false">Cancel</v-btn>
          <v-btn
            color="#d97706"
            dark
            class="text-capitalize elevation-1 rounded-lg"
            @click="closeScheme()"
            :loading="appLoading"
          >
            <v-icon left small>mdi-lock</v-icon>
            Confirm Closure
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ===== DELETE ENROLLMENT DIALOG ===== -->
    <v-dialog v-model="deletedialog" max-width="440px">
      <v-card class="modal-card rounded-lg pa-6">
        <div class="text-center">
          <div class="delete-icon-box mb-3 mx-auto">
            <v-icon size="26" color="#dc2626">mdi-trash-can-outline</v-icon>
          </div>
          <div class="delete-title mb-1">Remove Scheme Enrollment</div>
          <div class="delete-subtitle">
            Are you sure you want to remove this customer from the scheme? This record will be archived.
          </div>
        </div>
        <div class="d-flex align-center justify-end mt-6" style="gap: 8px;">
          <v-btn text class="btn-cancel" @click="deletedialog = false">Cancel</v-btn>
          <v-btn
            color="#dc2626"
            dark
            class="text-capitalize elevation-1 rounded-lg"
            @click="deleteScheme()"
            :loading="appLoading"
          >
            <v-icon left small>mdi-delete</v-icon>
            Confirm Delete
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ===== ADD NEW CUSTOMER MODAL ===== -->
    <v-dialog v-model="addcustomerdialogue" max-width="820px" persistent>
      <v-form ref="addcustomerform" @submit.prevent>
        <v-card class="modal-card rounded-lg overflow-hidden">
          <!-- Modal Header -->
          <div class="modal-header px-6 py-4 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <div class="modal-icon-pill mr-3" style="background: #fefce8; color: #9a6b27;">
                <v-icon size="18" color="#9a6b27">mdi-account-plus-outline</v-icon>
              </div>
              <div>
                <div class="modal-title">Create Retail Customer Profile</div>
                <div class="modal-subtitle">Add client details for instant scheme enlistment</div>
              </div>
            </div>
            <v-btn icon small @click="closeCustomerModal()" class="close-btn">
              <v-icon small color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Modal Body -->
          <div class="px-6 py-4">
            <div class="form-section-title mb-3">Profile &amp; Contact</div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <label class="field-label">Customer Name <span class="required-star">*</span></label>
                <v-text-field
                  v-model="newCustomer"
                  placeholder="Full customer name"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="field-label">Phone Number</label>
                <v-text-field
                  v-model="newContact"
                  placeholder="10-digit mobile number"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="phoneRules"
                  type="number"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="form-section-title mb-3 mt-3">Address &amp; Location</div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <label class="field-label">Address</label>
                <v-textarea
                  v-model="newAddress"
                  placeholder="Street address or locality"
                  rows="3"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-textarea>
              </v-col>
              <v-col cols="12" sm="6">
                <v-row dense>
                  <v-col cols="12">
                    <label class="field-label">City</label>
                    <v-text-field
                      v-model="newCity"
                      placeholder="City / Town"
                      outlined
                      dense
                      color="#9a6b27"
                      hide-details
                      class="pro-input"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <label class="field-label">Pin Code</label>
                    <v-text-field
                      v-model="newpinNumber"
                      placeholder="6-digit PIN"
                      type="number"
                      outlined
                      dense
                      color="#9a6b27"
                      :rules="pinRules"
                      hide-details="auto"
                      class="pro-input"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <div class="form-section-title mb-3 mt-3">Statutory KYC &amp; Financials</div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <label class="field-label">PAN Number</label>
                <v-text-field
                  v-model="newCustomerPAN"
                  placeholder="e.g. ABCDE1234F"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="panRules"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Aadhaar Number</label>
                <v-text-field
                  v-model="newCustomerAadhar"
                  placeholder="12-digit Aadhaar"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="aadhaarRules"
                  type="number"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Credit Limit (₹)</label>
                <v-text-field
                  v-model="newCreditLimit"
                  placeholder="0 = No limit enforced"
                  type="number"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Actions -->
          <div class="modal-footer px-6 py-3 border-t d-flex justify-end" style="gap: 8px;">
            <v-btn text class="btn-cancel" @click="closeCustomerModal()">Cancel</v-btn>
            <v-btn
              color="#9a6b27"
              class="btn-primary text-capitalize elevation-1"
              @click="validCustomer()"
              :loading="appLoading"
            >
              <v-icon left small>mdi-check</v-icon>
              Save Customer
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      page: 1,
      Pagelength: 1,
      totalCount: 0,

      // Search & Filters
      keyword: null,
      schemefilter: "",
      schemeList: [],
      List1: [],

      // Enlisting Form
      addScheme: false,
      addcat: false,
      customerList: [],
      customerdetails: "",
      goldSchemeName: "",
      schemeNumber: "",
      yrlyintrst: "",
      mnthlydpst: "",
      fromDate: "",
      secondDate: "",
      dueDate: "",
      menu1: false,
      menu3: false,
      selected: [],

      // Dialogs
      deletedialog: false,
      closedialog: false,
      curId: null,

      // Add Customer Form
      addcustomerdialogue: false,
      newCustomer: null,
      newCustomerPAN: null,
      newCustomerAadhar: null,
      newAddress: null,
      newCity: null,
      newpinNumber: null,
      newContact: null,
      newCreditLimit: null,

      // Validation Rules
      rules: {
        required: (value) => !!value || "Required field.",
        counter: (value) => (value && value.length <= 20) || "Max 20 characters",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail.";
        },
      },
      phoneRules: [
        (v) => !v || v.length === 10 || "Invalid phone number (10 digits).",
        (v) => !v || /^\d{10}$/.test(v) || "Phone number must contain digits only.",
      ],
      aadhaarRules: [
        (value) => !value || /^\d{12}$/.test(value) || "Invalid Aadhaar number (12 digits).",
      ],
      panRules: [
        (value) =>
          !value || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value) || "Invalid PAN number format (e.g. ABCDE1234F).",
      ],
      pinRules: [
        (value) => !value || /^\d{6}$/.test(value) || "Invalid PIN code (6 digits).",
      ],
    };
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    totalEnrollmentsCount() {
      return this.totalCount || (this.schemeList ? this.schemeList.length : 0);
    },
    activeAccountsCount() {
      if (!this.schemeList || !this.schemeList.length) return 0;
      return this.schemeList.filter((s) => s.schemeStatus === "Active").length;
    },
    totalRealizedAmount() {
      if (!this.schemeList || !this.schemeList.length) return 0;
      return this.schemeList.reduce((acc, curr) => acc + (Number(curr.finalTotalAmount) || 0), 0);
    },
    closedAccountsCount() {
      if (!this.schemeList || !this.schemeList.length) return 0;
      return this.schemeList.filter(
        (s) => s.schemeStatus === "Closed" || s.schemeStatus === "Matured"
      ).length;
    },
    computedMinDueDate() {
      if (this.fromDate) {
        const minDueDate = new Date(this.fromDate);
        minDueDate.setDate(1);
        minDueDate.setMonth(minDueDate.getMonth() + 1);
        minDueDate.setDate(1);
        return minDueDate.toISOString().substr(0, 10);
      }
      return null;
    },
    computedMaxDueDate() {
      if (this.fromDate) {
        const maxDueDate = new Date(this.fromDate);
        maxDueDate.setMonth(maxDueDate.getMonth() + 1);
        maxDueDate.setDate(1);
        const lastDay = new Date(
          maxDueDate.getFullYear(),
          maxDueDate.getMonth() + 1,
          0
        ).getDate();
        maxDueDate.setDate(lastDay);
        return maxDueDate.toISOString().substr(0, 10);
      }
      return null;
    },
  },
  watch: {
    keyword() {
      this.page = 1;
      this.getCustomerSchemeList();
    },
    page() {
      this.getCustomerSchemeList();
    },
    schemefilter() {
      this.page = 1;
      this.getCustomerSchemeList();
    },
    fromDate(newValue) {
      if (newValue) {
        const selectedDate = new Date(newValue);
        const futureDate = new Date(
          selectedDate.setMonth(selectedDate.getMonth() + 11)
        );
        this.secondDate = futureDate.toISOString().slice(0, 10);
      } else {
        this.secondDate = null;
      }
    },
  },
  mounted() {
    this.getCustomerSchemeList();
    this.getSchemeList();
  },
  methods: {
    getInitials(name) {
      if (!name) return "CU";
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(item) {
      if (!item) return "—";
      const dt = new Date(item);
      if (isNaN(dt.getTime())) return "—";
      return dt.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    openEnlistModal() {
      this.customerdetails = "";
      this.goldSchemeName = "";
      this.schemeNumber = "";
      this.yrlyintrst = "";
      this.mnthlydpst = "";
      this.fromDate = new Date().toISOString().slice(0, 10);
      this.dueDate = "";
      this.addScheme = true;
      this.getCustomer();
      this.getNextSchemeNumber();
    },
    closeEnlistModal() {
      this.addScheme = false;
      if (this.$refs.addcat) {
        this.$refs.addcat.resetValidation();
      }
    },
    openAddCustomerModal() {
      this.addcustomerdialogue = true;
    },
    closeCustomerModal() {
      this.addcustomerdialogue = false;
      this.resetCustomerForm();
    },
    resetCustomerForm() {
      this.newCustomer = null;
      this.newCustomerPAN = null;
      this.newCustomerAadhar = null;
      this.newAddress = null;
      this.newCity = null;
      this.newpinNumber = null;
      this.newContact = null;
      this.newCreditLimit = null;
      if (this.$refs.addcustomerform) {
        this.$refs.addcustomerform.resetValidation();
      }
    },
    selectThis(id) {
      this.selected = this.List1.filter((x) => x._id == id);
      if (this.selected && this.selected.length > 0) {
        this.yrlyintrst = this.selected[0].yearlyIntrest;
        this.mnthlydpst = this.selected[0].minmonthlydeposit;
      }
    },
    getNextSchemeNumber() {
      axios({
        method: "get",
        url: "/goldscheme/customer/next-schemenumber",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.schemeNumber = response.data.schemeNumber;
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    getCustomer() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/customer/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.customerList = response.data.data || [];
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    getCustomerSchemeList() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/goldscheme/customer/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          scheme: this.schemefilter,
          keyword: this.keyword,
          page: this.page,
          limit: 20,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.schemeList = response.data.data || [];
            const tot = response.data.totalLength || response.data.count || this.schemeList.length;
            this.totalCount = tot;
            this.Pagelength = Math.ceil(tot / 20) || 1;
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    getSchemeList() {
      axios({
        method: "get",
        url: "/goldscheme/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.List1 = response.data.data || [];
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    addToScheme() {
      if (!this.customerdetails) {
        this.msg = "Please select a customer.";
        this.showsnackbar = true;
        return;
      }
      if (!this.goldSchemeName) {
        this.msg = "Please select a gold scheme.";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        url: "/goldscheme/customer/add",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          customerId: this.customerdetails,
          goldSchemeId: this.goldSchemeName,
          matureDate: this.secondDate,
          startDate: this.fromDate,
          dueDate: this.dueDate,
          schemeNumber: this.schemeNumber,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.addScheme = false;
            this.customerdetails = "";
            this.yrlyintrst = "";
            this.fromDate = "";
            this.goldSchemeName = "";
            this.mnthlydpst = "";
            this.secondDate = "";
            this.schemeNumber = "";
            this.msg = response.data.msg || "Customer enrolled successfully!";
            this.showsnackbar = true;
            this.getCustomerSchemeList();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    AddToBalance(id) {
      this.appLoading = true;
      axios({
        url: "/goldscheme/addbalance/customer",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          payId: id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Payment remitted to ledger balance!";
            this.showsnackbar = true;
            this.getCustomerSchemeList();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    deleteScheme() {
      this.appLoading = true;
      axios({
        url: "/goldscheme/customer/delete",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId,
        },
      })
        .then((response) => {
          this.deletedialog = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Customer removed from scheme successfully!";
            this.showsnackbar = true;
            this.getCustomerSchemeList();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    closeScheme() {
      this.appLoading = true;
      axios({
        url: "/goldscheme/customer/close",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId,
        },
      })
        .then((response) => {
          this.closedialog = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Scheme closed early successfully!";
            this.showsnackbar = true;
            this.getCustomerSchemeList();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    validCustomer() {
      if (!this.newCustomer) {
        this.msg = "Please enter customer name.";
        this.showsnackbar = true;
        return;
      }
      this.AddCustomer();
    },
    AddCustomer() {
      this.appLoading = true;
      axios({
        url: "/customer/add",
        method: "POST",
        data: {
          customerName: this.newCustomer,
          panNo: this.newCustomerPAN,
          aadharNo: this.newCustomerAadhar,
          address: this.newAddress,
          city: this.newCity,
          mobileNumber: this.newContact,
          pinNumber: this.newpinNumber,
          creditLimit: this.newCreditLimit,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.addcustomerdialogue = false;
            this.getCustomer();
            if (response.data.customerData && response.data.customerData._id) {
              this.customerdetails = response.data.customerData._id;
            }
            this.msg = response.data.msg || "Customer added successfully!";
            this.showsnackbar = true;
            this.resetCustomerForm();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    customerFilter(item, queryText) {
      if (!item) return false;
      const textName = item.customerName ? item.customerName.toLowerCase() : "";
      const textPhone = item.mobileNumber ? String(item.mobileNumber).toLowerCase() : "";
      const searchText = queryText ? queryText.toLowerCase() : "";
      return textName.indexOf(searchText) > -1 || textPhone.indexOf(searchText) > -1;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap");

.customer-schemes-page-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.filter-select-wrap {
  width: 200px;
}

.search-input-wrap {
  width: 280px;
}

@media (max-width: 1100px) {
  .filter-select-wrap,
  .search-input-wrap {
    width: 100%;
  }
}

.filter-input :deep(.v-input__slot),
.search-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
}

.filter-input :deep(fieldset),
.search-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.filter-input.v-input--is-focused :deep(fieldset),
.search-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.refresh-icon-btn {
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  height: 38px !important;
  width: 38px !important;
}

.refresh-icon-btn:hover {
  background: #f8fafc !important;
}

.btn-gold-outlined {
  height: 38px !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  border-color: #9a6b27 !important;
  background: #fffdf9 !important;
}

.btn-gold-outlined:hover {
  background: #fefce8 !important;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

/* 4 KPI Metrics Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Master Data Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.pro-master-table table {
  width: 100%;
  border-collapse: collapse;
}

.pro-master-table th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pro-master-table td {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.master-table-row:hover {
  background-color: #f8fafc !important;
}

.customer-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 11.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.customer-name-text {
  font-weight: 600;
  color: #0f172a;
  font-size: 13.5px;
}

.customer-phone-text {
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
  margin-top: 1px;
}

.scheme-no-pill {
  font-family: monospace;
  font-size: 11.5px;
  font-weight: 700;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #1e293b;
  padding: 2px 7px;
  border-radius: 5px;
}

.scheme-name-text {
  font-weight: 700;
  color: #0f172a;
  font-size: 13px;
}

.date-info {
  font-size: 12px;
}

.total-amt-text {
  color: #0f172a;
  font-size: 13.5px;
}

.status-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-active {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.status-closed {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.status-matured {
  background: #fdf4ff;
  color: #7e22ce;
  border: 1px solid #e9d5ff;
}

.btn-row-action {
  font-size: 11.5px !important;
  font-weight: 700 !important;
  border-radius: 6px !important;
  height: 28px !important;
  padding: 0 10px !important;
}

.btn-row-action.pay-btn {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
}

.btn-row-action.remit-pay-btn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
}

.btn-row-action.remitted-btn {
  background: #ecfdf5 !important;
  border: 1px solid #a7f3d0 !important;
  color: #047857 !important;
}

.action-icon-btn {
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  width: 28px !important;
  height: 28px !important;
}

.action-icon-btn:hover {
  background: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
}

.action-icon-btn.lock-btn:hover {
  background: #fefce8 !important;
  border-color: #fef08a !important;
}

.action-icon-btn.delete-btn:hover {
  background: #fef2f2 !important;
  border-color: #fca5a5 !important;
}

.border-t {
  border-top: 1px solid #f1f5f9;
}

.text-muted {
  color: #94a3b8;
}

/* Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 440px;
  margin-left: auto;
  margin-right: auto;
}

/* Modal Dialogs */
.modal-card {
  background: #ffffff !important;
}

.modal-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.modal-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  font-size: 12px;
  color: #64748b;
}

.modal-icon-pill {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.form-section-title {
  font-size: 11.5px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}

.required-star {
  color: #dc2626;
}

.pro-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 6px !important;
  font-size: 13px !important;
}

.pro-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.pro-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
}

.btn-cancel {
  border-radius: 8px !important;
  color: #64748b !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}

.lock-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fffbeb;
  border: 1px solid #fde68a;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fef2f2;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.delete-subtitle {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
  max-width: 360px;
  margin: 0 auto;
}
</style>